
import { useEffect, useState } from "react";
import { Plus, Loader2, Check, X } from "lucide-react";

import {
  getWorkspaceMembers,
  addWorkspaceMember,
} from "../services/workspaceService";

import { getEmployees } from "../services/employeeService";

const WorkspaceMembers = ({ workspace }) => {
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const [error, setError] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const workspaceUuid =
    workspace?.uuid ||
    workspace?.workspace_uuid ||
    workspace?.id;

  // ==========================================================
  // FETCH WORKSPACE MEMBERS
  // ==========================================================

  const fetchWorkspaceMembers = async () => {
    if (!workspaceUuid) return;

    try {
      setLoading(true);
      setError("");

      const response = await getWorkspaceMembers(workspaceUuid);

      console.log("WORKSPACE MEMBERS:", response);

      const memberList = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];

      setWorkspaceMembers(memberList);
    } catch (error) {
      console.error("FAILED TO FETCH WORKSPACE MEMBERS:", error);

      setWorkspaceMembers([]);

      setError(
        error.response?.data?.message ||
          "Failed to load workspace members."
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // FETCH EMPLOYEES
  // ==========================================================

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);

      const response = await getEmployees();

      console.log("EMPLOYEES:", response);

      const employeeList = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];

      setEmployees(employeeList);
    } catch (error) {
      console.error("FAILED TO FETCH EMPLOYEES:", error);

      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  };

  // ==========================================================
  // LOAD DATA
  // ==========================================================

  useEffect(() => {
    if (!workspaceUuid) return;

    fetchWorkspaceMembers();
    fetchEmployees();
  }, [workspaceUuid]);

  // ==========================================================
  // FIND EMPLOYEE
  // ==========================================================

  const findEmployee = (employeeUuid) => {
    return employees.find(
      (employee) =>
        employee.uuid === employeeUuid ||
        employee.employee_uuid === employeeUuid ||
        employee.id === employeeUuid
    );
  };

  // ==========================================================
  // EMPLOYEE NAME
  // ==========================================================

  const getEmployeeName = (employeeUuid) => {
    const employee = findEmployee(employeeUuid);

    if (!employee) {
      return "Employee";
    }

    return (
      employee.name ||
      employee.employee_name ||
      employee.full_name ||
      employee.employeeName ||
      "Employee"
    );
  };

  // ==========================================================
  // EMPLOYEE EMAIL
  // ==========================================================

  const getEmployeeEmail = (employeeUuid) => {
    const employee = findEmployee(employeeUuid);

    if (!employee) {
      return "";
    }

    return employee.email || employee.employee_email || "";
  };

  // ==========================================================
  // EMPLOYEE DEPARTMENT
  // ==========================================================

  const getEmployeeDepartment = (employeeUuid) => {
    const employee = findEmployee(employeeUuid);

    if (!employee) {
      return "";
    }

    return (
      employee.department ||
      employee.department_name ||
      ""
    );
  };

  // ==========================================================
  // CHECK EXISTING MEMBER
  // ==========================================================

  const isAlreadyMember = (employeeUuid) => {
    return workspaceMembers.some(
      (member) =>
        member.employee_uuid === employeeUuid &&
        member.status !== "Removed"
    );
  };

  // ==========================================================
  // AVAILABLE EMPLOYEES
  // ==========================================================

  const availableEmployees = employees.filter((employee) => {
    const employeeUuid =
      employee.uuid ||
      employee.employee_uuid ||
      employee.id;

    return !isAlreadyMember(employeeUuid);
  });

  // ==========================================================
  // SELECT / UNSELECT EMPLOYEE
  // ==========================================================

  const toggleEmployee = (employee) => {
    const employeeUuid =
      employee.uuid ||
      employee.employee_uuid ||
      employee.id;

    setSelectedEmployees((previous) => {
      if (previous.includes(employeeUuid)) {
        return previous.filter(
          (uuid) => uuid !== employeeUuid
        );
      }

      return [...previous, employeeUuid];
    });
  };

  // ==========================================================
  // ADD MEMBERS
  // ==========================================================

  const handleAddMembers = async () => {
    if (
      !workspaceUuid ||
      selectedEmployees.length === 0
    ) {
      return;
    }

    try {
      setAdding(true);
      setError("");

      const requests = selectedEmployees.map(
        (employeeUuid) => {
          const employee =
            findEmployee(employeeUuid);

          const employeeRole =
            employee?.role ||
            employee?.designation ||
            employee?.job_title ||
            "Team Member";

          return addWorkspaceMember(
            workspaceUuid,
            {
              project_uuid:
                workspace?.project_uuid,

              employee_uuid:
                employeeUuid,

              workspace_role:
                employeeRole,

              assigned_by_employee_uuid:
                employeeUuid,

              assigned_on: new Date()
                .toISOString()
                .split("T")[0],

              status: "Active",
            }
          );
        }
      );

      await Promise.all(requests);

      // Clear selection
      setSelectedEmployees([]);

      // Close add-member panel
      setShowAddMember(false);

      // Fetch latest backend data
      await fetchWorkspaceMembers();
    } catch (error) {
      console.error(
        "FAILED TO ADD MEMBERS:",
        error
      );

      setError(
        error.response?.data?.message ||
          "Failed to add members."
      );
    } finally {
      setAdding(false);
    }
  };

  // ==========================================================
  // CANCEL
  // ==========================================================

  const handleCancelAdd = () => {
    setSelectedEmployees([]);
    setShowAddMember(false);
  };

  if (!workspace) {
    return null;
  }

  return (
    <div className="space-y-6">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex items-center justify-between">

        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Workspace Members
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Team members working in{" "}
            {workspace.workspace_name ||
              workspace.name ||
              "this workspace"}
          </p>
        </div>

        <div className="flex items-center gap-3">

          {/* MEMBER COUNT */}

          <div className="rounded-lg bg-blue-50 px-4 py-2">
            <span className="font-semibold text-blue-600">
              {workspaceMembers.length} Members
            </span>
          </div>

          {/* ADD MEMBER */}

          <button
            type="button"
            onClick={() =>
              setShowAddMember(true)
            }
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <Plus size={17} />
            Add Members
          </button>

        </div>
      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* =====================================================
          ADD MEMBER PANEL
      ===================================================== */}

      {showAddMember && (
        <div className="rounded-xl border border-blue-200 bg-white p-5 shadow-sm">

          <div className="flex items-center justify-between">

            <div>
              <h3 className="text-lg font-semibold text-slate-800">
                Add Members
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                Select one or more employees to add.
              </p>
            </div>

            <button
              type="button"
              onClick={handleCancelAdd}
              className="rounded-lg p-2 hover:bg-slate-100"
            >
              <X size={20} />
            </button>

          </div>

          {/* EMPLOYEE LIST */}

          <div className="mt-5 max-h-72 overflow-y-auto">

            {employeesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2
                  size={22}
                  className="animate-spin text-blue-600"
                />
              </div>
            ) : availableEmployees.length === 0 ? (
              <div className="py-8 text-center">

                <p className="font-medium text-slate-700">
                  No employees available
                </p>

                <p className="mt-1 text-sm text-slate-500">
                  All employees are already members
                  of this workspace.
                </p>

              </div>
            ) : (
              <div className="space-y-2">

                {availableEmployees.map(
                  (employee) => {
                    const employeeUuid =
                      employee.uuid ||
                      employee.employee_uuid ||
                      employee.id;

                    const selected =
                      selectedEmployees.includes(
                        employeeUuid
                      );

                    return (
                      <button
                        type="button"
                        key={employeeUuid}
                        onClick={() =>
                          toggleEmployee(employee)
                        }
                        className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition ${
                          selected
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >

                        {/* CHECKBOX */}

                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded border ${
                            selected
                              ? "border-blue-600 bg-blue-600 text-white"
                              : "border-slate-300"
                          }`}
                        >
                          {selected && (
                            <Check size={14} />
                          )}
                        </div>

                        {/* AVATAR */}

                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700">
                          {getEmployeeName(
                            employeeUuid
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>

                        {/* EMPLOYEE INFO */}

                        <div className="flex-1">

                          <p className="font-semibold text-slate-800">
                            {getEmployeeName(
                              employeeUuid
                            )}
                          </p>

                          <p className="text-sm text-slate-500">
                            {getEmployeeEmail(
                              employeeUuid
                            )}
                          </p>

                          <p className="text-xs text-blue-600">
                            {getEmployeeDepartment(
                              employeeUuid
                            )}
                          </p>

                        </div>

                      </button>
                    );
                  }
                )}

              </div>
            )}

          </div>

          {/* ACTIONS */}

          <div className="mt-5 flex items-center justify-between border-t pt-4">

            <p className="text-sm text-slate-500">
              {selectedEmployees.length} selected
            </p>

            <div className="flex gap-3">

              <button
                type="button"
                onClick={handleCancelAdd}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={
                  adding ||
                  selectedEmployees.length === 0
                }
                onClick={handleAddMembers}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >

                {adding && (
                  <Loader2
                    size={16}
                    className="animate-spin"
                  />
                )}

                {adding
                  ? "Adding..."
                  : "Add Members"}

              </button>

            </div>
          </div>

        </div>
      )}

      {/* =====================================================
          MEMBERS
      ===================================================== */}

      {loading ? (
        <div className="flex items-center justify-center py-12">

          <div className="flex items-center gap-2 text-sm text-slate-500">

            <Loader2
              size={20}
              className="animate-spin"
            />

            Loading members...

          </div>
        </div>

      ) : workspaceMembers.length === 0 ? (

        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">

          <h3 className="text-lg font-semibold text-slate-700">
            No members found
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Add members to this workspace.
          </p>

        </div>

      ) : (

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

          {workspaceMembers.map((member) => {

            const employeeName =
              getEmployeeName(
                member.employee_uuid
              );

            return (
              <div
                key={member.uuid}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >

                <div className="flex items-center gap-4">

                  {/* AVATAR */}

                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-100 text-lg font-bold text-blue-700">
                    {employeeName
                      .charAt(0)
                      .toUpperCase()}
                  </div>

                  {/* INFO */}

                  <div className="min-w-0 flex-1">

                    <h3 className="truncate text-lg font-semibold text-slate-800">
                      {employeeName}
                    </h3>

                    <p className="text-sm text-slate-500">
                      {member.workspace_role ||
                        "Team Member"}
                    </p>

                    <p className="mt-1 text-sm text-blue-600">
                      {getEmployeeDepartment(
                        member.employee_uuid
                      )}
                    </p>

                  </div>

                  {/* STATUS */}

                  <span
                    className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
                      member.status === "Active"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {member.status}
                  </span>

                </div>

                {/* ASSIGNED DATE */}

                <div className="mt-5 rounded-lg bg-slate-50 p-3">

                  <p className="text-xs text-slate-500">
                    Assigned On
                  </p>

                  <p className="mt-1 text-sm font-semibold text-slate-800">
                    {member.assigned_on
                      ? new Date(
                          member.assigned_on
                        ).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )
                      : "-"}
                  </p>

                </div>

              </div>
            );
          })}

        </div>
      )}

    </div>
  );
};

export default WorkspaceMembers;

